import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { IoSend } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import useScrollToTop from '../hooks/useScrollToTop';
import { getTicket, getTicketMessages, sendTicketMessage, closeTicket } from '../services/support';
import { toast } from 'react-toastify';

const TicketChat = () => {
	useScrollToTop();
	const navigate = useNavigate();
	const { ticketId } = useParams();
	const { t } = useTranslation();
	const [messages, setMessages] = useState([]);
	const [newMessage, setNewMessage] = useState('');
	const [loading, setLoading] = useState(true);
	const [sending, setSending] = useState(false);
	const [ticketStatus, setTicketStatus] = useState('open');
	const messagesEndRef = useRef(null);
	const messageInputRef = useRef(null);
	const [ticket, setTicket] = useState(null);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const ticketData = await getTicket(ticketId);
				setTicket(ticketData);

				setTicketStatus(ticketData.status);
				const messagesData = await getTicketMessages(ticketId);
				setMessages(messagesData.map(msg => ({
					...msg,
					text: msg.message,
					timestamp: msg.created_at
				})));
			} catch (e) {
				toast.error('Ошибка загрузки тикета или сообщений');
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, [ticketId]);

	useEffect(() => {
		// Прокрутка к последнему сообщению
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	useEffect(() => {
		if (window.Telegram && window.Telegram.WebApp) {
			window.Telegram.WebApp.BackButton.show();
			window.Telegram.WebApp.BackButton.onClick(() => navigate('/support'));
		}
		return () => {
			if (window.Telegram && window.Telegram.WebApp) {
				window.Telegram.WebApp.BackButton.hide();
				window.Telegram.WebApp.BackButton.offClick();
			}
		};
	}, [navigate]);

	const handleBack = () => {
		navigate('/support');
	};

	const handleSendMessage = async () => {
		if (!newMessage.trim() || sending) return;
		setSending(true);
		try {
			await sendTicketMessage(ticketId, newMessage.trim());
			setNewMessage('');
			// Обновить сообщения
			const data = await getTicketMessages(ticketId);
			setMessages(data.map(msg => ({
				...msg,
				text: msg.message,
				timestamp: msg.created_at
			})));
		} catch (e) {
			toast.error('Ошибка отправки сообщения');
		} finally {
			setSending(false);
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	const formatTime = (timestamp) => {
		const date = new Date(timestamp);
		return date.toLocaleTimeString('ru-RU', {
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const formatDate = (timestamp) => {
		const date = new Date(timestamp);
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (date.toDateString() === today.toDateString()) {
			return t('support.today');
		} else if (date.toDateString() === yesterday.toDateString()) {
			return t('support.yesterday');
		} else {
			return date.toLocaleDateString('ru-RU');
		}
	};

	const renderMessage = (message, index) => {
		const isUser = message.sender === 'user';
		const showDate = index === 0 ||
			formatDate(messages[index - 1]?.timestamp) !== formatDate(message.timestamp);

		return (
			<React.Fragment key={message.id}>
				{showDate && (
					<DateDivider>
						{formatDate(message.timestamp)}
					</DateDivider>
				)}
				<MessageContainer isUser={isUser}>
					<MessageBubble isUser={isUser}>
						<MessageText>{message.text}</MessageText>
						<MessageTime>{formatTime(message.timestamp)}</MessageTime>
					</MessageBubble>
				</MessageContainer>
			</React.Fragment>
		);
	};

	const handleInputGrow = (e) => {
		const ta = e.target;
		ta.style.height = '32px';
		ta.style.height = ta.scrollHeight + 'px';
	};

	const handleCloseTicket = async () => {
		try {
			await closeTicket(ticketId);
			setTicketStatus('closed');
			toast.success('Тикет закрыт');
		} catch (e) {
			toast.error('Ошибка при закрытии тикета');
		}
	};

	return (
		<Container>
			<Header>
				<HeaderInfo>
					<HeaderTitle>{ticket?.title || (ticket ? t('support.topics.' + ticket.theme) : ticketId)}</HeaderTitle>
					<HeaderSubtitle>{t('support.chat.supportTitle')}</HeaderSubtitle>
				</HeaderInfo>
				<StatusBadge status={ticketStatus}>
					{ticketStatus === 'open' ? t('support.status.open') : t('support.status.closed')}
				</StatusBadge>
				{ticketStatus === 'open' && (
					<CloseTicketButton onClick={handleCloseTicket}>
						{t('support.closeTicket')}
					</CloseTicketButton>
				)}
			</Header>

			<MessagesContainer>
				{loading ? (
					<LoadingContainer>
						<LoadingText>{t('support.loadingMessages')}</LoadingText>
					</LoadingContainer>
				) : (
					<>
						{messages.map((message, index) => renderMessage(message, index))}
						<div ref={messagesEndRef} />
					</>
				)}
			</MessagesContainer>

			<InputContainer>
				<MessageInput
					ref={messageInputRef}
					value={newMessage}
					onChange={(e) => setNewMessage(e.target.value)}
					onInput={handleInputGrow}
					onKeyPress={handleKeyPress}
					placeholder={t('support.inputPlaceholder')}
					disabled={sending || ticketStatus === 'closed'}
				/>
				<SendButton
					onClick={handleSendMessage}
					disabled={!newMessage.trim() || sending || ticketStatus === 'closed'}
				>
					{sending ? (
						<LoadingSpinner />
					) : (
						<IoSend size={20} />
					)}
				</SendButton>
			</InputContainer>
		</Container>
	);
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${props => props.theme.background};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.text.primary};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  margin-right: 12px;
  
  &:active {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const HeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text.primary};
  margin-bottom: 2px;
`;

const HeaderSubtitle = styled.div`
  font-size: 12px;
  color: ${props => props.theme.text.secondary};
`;

const StatusBadge = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.status === 'open' ? '#34c759' : '#8e8e93'};
  background: ${props => props.status === 'open' ? '#34c75920' : '#8e8e9320'};
  padding: 4px 8px;
  border-radius: 6px;
  white-space: nowrap;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 16px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const LoadingText = styled.div`
  color: ${props => props.theme.text.secondary};
  font-size: 14px;
`;

const DateDivider = styled.div`
  text-align: center;
  color: ${props => props.theme.text.secondary};
  font-size: 12px;
  margin: 16px 0 8px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: ${props => props.theme.border};
    z-index: -1;
  }
  
  span {
    background: ${props => props.theme.background};
    padding: 0 12px;
  }
`;

const MessageContainer = styled.div`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  margin-bottom: 8px;
`;

const MessageBubble = styled.div`
  max-width: 70%;
  background: ${props => props.isUser ? props.theme.primary : props.theme.card.background};
  color: ${props => props.isUser ? 'white' : props.theme.text.primary};
  padding: 12px 16px;
  border-radius: 18px;
  border-bottom-right-radius: ${props => props.isUser ? '4px' : '18px'};
  border-bottom-left-radius: ${props => props.isUser ? '18px' : '4px'};
  position: relative;
`;

const MessageText = styled.div`
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 4px;
  word-wrap: break-word;
  white-space: pre-line;
`;

const MessageTime = styled.div`
  font-size: 11px;
  opacity: 0.7;
  text-align: ${props => props.isUser ? 'right' : 'left'};
`;

const InputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 16px;
  background: ${props => props.theme.card.background};
  border-top: 1px solid ${props => props.theme.border};
  position: sticky;
  bottom: 0;
  z-index: 10;
`;

const MessageInput = styled.textarea`
  flex: 1;
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 20px;
  padding: 12px 16px;
  color: ${props => props.theme.text.primary};
  font-size: 14px;
  resize: none;
  min-height: 32px;
  max-height: 120px;
  outline: none;
  overflow-y: auto;
  &::placeholder {
    color: ${props => props.theme.text.secondary};
  }
  &:disabled {
    opacity: 0.6;
  }
`;

const SendButton = styled.button`
  background: ${props => props.disabled ? props.theme.text.secondary : props.theme.primary};
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CloseTicketButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.text.primary};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  margin-left: 12px;
  &:active {
    background: rgba(255, 255, 255, 0.1);
  }
`;

export default TicketChat;